/**
 * Fonctions d'accès, une par route du back. Aucune logique ici : ces
 * fonctions décrivent le contrat, `queries.ts` les branche sur le cache.
 */
import { api, download, upload } from './client'
import type {
  Alert,
  Anomaly,
  Attachment,
  AuditLog,
  CloseShiftInput,
  Customer,
  CustomerInput,
  Delivery,
  DeliveryInput,
  Envelope,
  Equipment,
  EquipmentInput,
  FuelProduct,
  FuelProductInput,
  Invoice,
  InvoiceInput,
  InvoicePayment,
  InvoicePaymentInput,
  Nozzle,
  NozzleInput,
  NozzleUpdateInput,
  LoginResponse,
  MaintenanceRecord,
  MaintenanceRecordInput,
  ModuleState,
  PermissionInfo,
  OpenShiftInput,
  Paginated,
  PaymentList,
  Pump,
  PumpInput,
  PumpUpdateInput,
  PurchaseOrder,
  PurchaseOrderInput,
  ReportAgent,
  ReportDay,
  ReportOverview,
  ReportQuery,
  ReportStation,
  ShopArticle,
  ShopArticleInput,
  ShopInventory,
  ShopInventoryInput,
  ShopMovementInput,
  ShopSale,
  ShopSaleInput,
  ShopStock,
  RoleInput,
  RoleSummary,
  SiteInspection,
  SiteInspectionInput,
  Station,
  StationInput,
  Supplier,
  Tank,
  TankGauging,
  TankGaugingInput,
  TankInput,
  TankUpdateInput,
  User,
  UserInput,
  Voucher,
  VoucherBatch,
  VoucherBatchInput,
  WorkShift,
} from './types'

/** Toutes les listes paginées acceptent `per_page` (plafonné à 100 par le back). */
type ListQuery = { page?: number; per_page?: number }

export const endpoints = {
  auth: {
    login: (email: string, password: string) =>
      api.post<LoginResponse>('/v1/auth/login', { email, password, device_name: 'console-web' }),
    logout: () => api.post<{ message: string }>('/v1/auth/logout'),
    me: () => api.get<Envelope<User>>('/v1/me'),
  },

  modules: {
    list: () => api.get<Envelope<ModuleState[]>>('/v1/modules'),
    update: (key: string, is_enabled: boolean) =>
      api.put<Envelope<ModuleState>>(`/v1/modules/${key}`, { is_enabled }),
  },

  /**
   * Configuration des rôles. La lecture suffit à `users.manage` — attribuer un
   * rôle suppose de savoir ce qu'il contient ; la composition exige
   * `roles.manage`, réservée au superadmin.
   */
  roles: {
    list: () => api.get<Envelope<RoleSummary[]>>('/v1/roles'),
    get: (id: number) => api.get<Envelope<RoleSummary>>(`/v1/roles/${id}`),
    create: (input: RoleInput) => api.post<Envelope<RoleSummary>>('/v1/roles', input),
    update: (id: number, input: RoleInput) =>
      api.put<Envelope<RoleSummary>>(`/v1/roles/${id}`, input),
    remove: (id: number) => api.delete<{ message: string }>(`/v1/roles/${id}`),
  },

  permissions: {
    list: () => api.get<Envelope<PermissionInfo[]>>('/v1/permissions'),
  },

  users: {
    list: (q?: ListQuery) => api.get<Paginated<User>>('/v1/users', q),
    create: (input: UserInput) => api.post<Envelope<User>>('/v1/users', input),
    update: (id: number, input: UserInput) => api.put<Envelope<User>>(`/v1/users/${id}`, input),
  },

  stations: {
    list: (q?: ListQuery) => api.get<Paginated<Station>>('/v1/stations', q),
    get: (id: number) => api.get<Envelope<Station>>(`/v1/stations/${id}`),
    create: (input: StationInput) => api.post<Envelope<Station>>('/v1/stations', input),
    update: (id: number, input: StationInput) =>
      api.put<Envelope<Station>>(`/v1/stations/${id}`, input),
    archive: (id: number) => api.delete<{ message: string }>(`/v1/stations/${id}`),
  },

  inventory: {
    products: () => api.get<Envelope<FuelProduct[]>>('/v1/fuel-products'),
    tanks: (q?: ListQuery) => api.get<Paginated<Tank>>('/v1/tanks', q),
    tank: (id: number) => api.get<Envelope<Tank>>(`/v1/tanks/${id}`),
    gauge: (input: TankGaugingInput) => api.post<Envelope<TankGauging>>('/v1/tank-gaugings', input),

    /**
     * Matériel d'une station. Déclarer une pompe ou changer un prix engage
     * tout le réseau : ces appels exigent des permissions distinctes de
     * l'exploitation quotidienne.
     */
    pumps: (q?: ListQuery) => api.get<Paginated<Pump>>('/v1/pumps', q),
    createPump: (input: PumpInput) => api.post<Envelope<Pump>>('/v1/pumps', input),
    updatePump: (id: number, input: PumpUpdateInput) =>
      api.put<Envelope<Pump>>(`/v1/pumps/${id}`, input),
    archivePump: (id: number) => api.delete<{ message: string }>(`/v1/pumps/${id}`),
    createNozzle: (input: NozzleInput) => api.post<Envelope<Nozzle>>('/v1/nozzles', input),
    updateNozzle: (id: number, input: NozzleUpdateInput) =>
      api.put<Envelope<Nozzle>>(`/v1/nozzles/${id}`, input),
    archiveNozzle: (id: number) => api.delete<{ message: string }>(`/v1/nozzles/${id}`),
    createTank: (input: TankInput) => api.post<Envelope<Tank>>('/v1/tanks', input),
    updateTank: (id: number, input: TankUpdateInput) =>
      api.put<Envelope<Tank>>(`/v1/tanks/${id}`, input),
    archiveTank: (id: number) => api.delete<{ message: string }>(`/v1/tanks/${id}`),
    createProduct: (input: FuelProductInput) =>
      api.post<Envelope<FuelProduct>>('/v1/fuel-products', input),
    updateProduct: (id: number, input: FuelProductInput) =>
      api.put<Envelope<FuelProduct>>(`/v1/fuel-products/${id}`, input),
  },

  shifts: {
    list: (q?: ListQuery) => api.get<Paginated<WorkShift>>('/v1/work-shifts', q),
    get: (id: number) => api.get<Envelope<WorkShift>>(`/v1/work-shifts/${id}`),
    open: (input: OpenShiftInput) => api.post<Envelope<WorkShift>>('/v1/work-shifts', input),
    close: (id: number, input: CloseShiftInput) =>
      api.post<Envelope<WorkShift>>(`/v1/work-shifts/${id}/close`, input),
    approve: (id: number) => api.post<Envelope<WorkShift>>(`/v1/work-shifts/${id}/approve`),
  },

  procurement: {
    suppliers: () => api.get<Envelope<Supplier[]>>('/v1/suppliers'),
    orders: (q?: ListQuery) => api.get<Paginated<PurchaseOrder>>('/v1/purchase-orders', q),
    createOrder: (input: PurchaseOrderInput) =>
      api.post<Envelope<PurchaseOrder>>('/v1/purchase-orders', input),
    deliveries: (q?: ListQuery) => api.get<Paginated<Delivery>>('/v1/deliveries', q),
    createDelivery: (input: DeliveryInput) => api.post<Envelope<Delivery>>('/v1/deliveries', input),
  },

  customers: {
    list: (q?: ListQuery) => api.get<Paginated<Customer>>('/v1/customers', q),
    get: (id: number) => api.get<Envelope<Customer>>(`/v1/customers/${id}`),
    create: (input: CustomerInput) => api.post<Envelope<Customer>>('/v1/customers', input),
    update: (id: number, input: CustomerInput) =>
      api.put<Envelope<Customer>>(`/v1/customers/${id}`, input),
    invoices: (q?: ListQuery) => api.get<Paginated<Invoice>>('/v1/invoices', q),
    createInvoice: (input: InvoiceInput) => api.post<Envelope<Invoice>>('/v1/invoices', input),

    /**
     * Règlements d'une facture. Le montant réglé n'est pas une saisie : il est
     * la somme des règlements, et le serveur en déduit le statut.
     */
    payments: (invoiceId: number) =>
      api.get<PaymentList>(`/v1/invoices/${invoiceId}/payments`),
    recordPayment: (invoiceId: number, input: InvoicePaymentInput) =>
      api.post<Envelope<InvoicePayment>>(`/v1/invoices/${invoiceId}/payments`, input),
  },

  /**
   * Totaux du réseau. Les sommes sont faites en base : les recalculer ici
   * sur une page de résultats donnerait un chiffre faux, et faux en silence.
   */
  reports: {
    overview: (q?: ReportQuery) => api.get<Envelope<ReportOverview>>('/v1/reports/overview', q),
    daily: (q?: ReportQuery) => api.get<Envelope<ReportDay[]>>('/v1/reports/daily', q),
    stations: (q?: ReportQuery) => api.get<Envelope<ReportStation[]>>('/v1/reports/stations', q),
    agents: (q?: ReportQuery) => api.get<Envelope<ReportAgent[]>>('/v1/reports/agents', q),
    export: (report: 'daily' | 'stations' | 'agents', q?: ReportQuery) =>
      download(`/v1/reports/${report}/export`, q),
  },

  /**
   * Boutique. Le stock est la somme d'un journal : aucune de ces routes
   * n'accepte une quantité de stock en écriture directe.
   */
  /**
   * Dépôt des pièces justificatives, premier temps du relevé : la photo
   * d'abord, le relevé ensuite, avec le `path` que le serveur renvoie.
   */
  attachments: {
    upload: (file: File, clientReference?: string) =>
      upload<Envelope<Attachment>>(
        '/v1/attachments',
        file,
        clientReference ? { client_reference: clientReference } : {},
      ),
  },

  shop: {
    articles: (q?: ListQuery & { category?: string; active_only?: boolean }) =>
      api.get<Paginated<ShopArticle>>('/v1/shop/articles', q),
    createArticle: (input: ShopArticleInput) =>
      api.post<Envelope<ShopArticle>>('/v1/shop/articles', input),
    updateArticle: (id: number, input: ShopArticleInput) =>
      api.put<Envelope<ShopArticle>>(`/v1/shop/articles/${id}`, input),
    stocks: (q?: ListQuery & { station_id?: number; below_minimum?: boolean }) =>
      api.get<Paginated<ShopStock>>('/v1/shop/stocks', q),
    createMovement: (input: ShopMovementInput) =>
      api.post<Envelope<ShopStock>>('/v1/shop/movements', input),
    sales: (q?: ListQuery & { station_id?: number }) =>
      api.get<Paginated<ShopSale>>('/v1/shop/sales', q),
    createSale: (input: ShopSaleInput) => api.post<Envelope<ShopSale>>('/v1/shop/sales', input),
    inventories: (q?: ListQuery) => api.get<Paginated<ShopInventory>>('/v1/shop/inventories', q),
    createInventory: (input: ShopInventoryInput) =>
      api.post<Envelope<ShopInventory>>('/v1/shop/inventories', input),
  },

  vouchers: {
    list: (q?: ListQuery) => api.get<Paginated<Voucher>>('/v1/vouchers', q),
    get: (code: string) => api.get<Envelope<Voucher>>(`/v1/vouchers/${code}`),
    cancel: (code: string, reason: string) =>
      api.post<Envelope<Voucher>>(`/v1/vouchers/${code}/cancel`, { reason }),
    batches: (q?: ListQuery) => api.get<Paginated<VoucherBatch>>('/v1/voucher-batches', q),
    batch: (id: number) => api.get<Envelope<VoucherBatch>>(`/v1/voucher-batches/${id}`),
    createBatch: (input: VoucherBatchInput) =>
      api.post<Envelope<VoucherBatch>>('/v1/voucher-batches', input),
  },

  risk: {
    anomalies: (q?: ListQuery) => api.get<Paginated<Anomaly>>('/v1/anomalies', q),
    anomaly: (id: number) => api.get<Envelope<Anomaly>>(`/v1/anomalies/${id}`),
    resolve: (id: number, resolution: string) =>
      api.post<Envelope<Anomaly>>(`/v1/anomalies/${id}/resolve`, { resolution }),
    alerts: (q?: ListQuery) => api.get<Paginated<Alert>>('/v1/alerts', q),
    markRead: (id: number) => api.post<{ message: string }>(`/v1/alerts/${id}/read`),
  },

  maintenance: {
    equipment: (q?: ListQuery) => api.get<Paginated<Equipment>>('/v1/equipment', q),
    createEquipment: (input: EquipmentInput) =>
      api.post<Envelope<Equipment>>('/v1/equipment', input),
    records: (q?: ListQuery) => api.get<Paginated<MaintenanceRecord>>('/v1/maintenance-records', q),
    createRecord: (input: MaintenanceRecordInput) =>
      api.post<Envelope<MaintenanceRecord>>('/v1/maintenance-records', input),
  },

  inspections: {
    list: (q?: ListQuery) => api.get<Paginated<SiteInspection>>('/v1/site-inspections', q),
    create: (input: SiteInspectionInput) =>
      api.post<Envelope<SiteInspection>>('/v1/site-inspections', input),
  },

  audit: {
    list: (q?: ListQuery & { event?: string; auditable_type?: string; user_id?: number }) =>
      api.get<Paginated<AuditLog>>('/v1/audit-logs', q),
  },
}
